import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { P, PermissionGuard } from '@/features/permissions';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useRole,
  useUpdateRole,
  useDeleteRole,
  useSetRolePermissions,
  usePermissions as useAllPermissions,
} from '../api/roles-api';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export function RoleDetailPage() {
  return (
    <PermissionGuard permission={P.MANAGE_USERS}>
      <RoleDetailPageContent />
    </PermissionGuard>
  );
}

function RoleDetailPageContent() {
  const params = useParams({ strict: false }) as { roleId: string };
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();

  const { isLoading, data: role } = useRole(params.roleId);
  const { data: allPermissions } = useAllPermissions();
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const setRolePermissions = useSetRolePermissions();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());

  const isSaving = updateRole.isPending || setRolePermissions.isPending;

  useEffect(() => {
    setPageHeader({
      title: role?.name ?? 'Role Detail',
      actions: (
        <div className="flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="destructive">
                <Trash2 className="h-4 w-4" />
                <span className="hidden md:inline">Delete</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Role</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Are you sure? This role will be permanently deleted. Roles with assigned members cannot be deleted.
              </p>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  disabled={deleteRole.isPending}
                  onClick={handleDelete}
                >
                  {deleteRole.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button size="sm" disabled={isSaving} onClick={handleSave}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            <span className="hidden md:inline">Save</span>
          </Button>
        </div>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, role?.name, isSaving, deleteRole.isPending]);

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description ?? '');
      setSelectedPermissions(new Set(role.permissions.map((p) => p.id)));
    }
  }, [role]);

  async function handleSave() {
    if (!name.trim()) return;
    try {
      await updateRole.mutateAsync({
        id: params.roleId,
        name: name.trim(),
        description: description.trim() || null,
      });
      await setRolePermissions.mutateAsync({
        roleId: params.roleId,
        permissionIds: Array.from(selectedPermissions),
      });
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    }
  }

  async function handleDelete() {
    try {
      await deleteRole.mutateAsync(params.roleId);
      toast.success('Role deleted');
      navigate({ to: '/roles' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete role';
      toast.error(message.includes('409') ? 'Cannot delete role while members are assigned' : message);
    }
  }

  function togglePermission(permId: string) {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Role not found.</p>
      </div>
    );
  }

  // Group permissions by resource
  const permissionsByResource = (allPermissions ?? []).reduce<
    Record<string, typeof allPermissions>
  >((acc, perm) => {
    const resource = perm.resource ?? 'other';
    if (!acc[resource]) acc[resource] = [];
    acc[resource]!.push(perm);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <Card>
        <CardHeader>
          <CardTitle>Role Details</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              value={name}
              placeholder="Role name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="role-desc">Description</Label>
            <Textarea
              id="role-desc"
              rows={2}
              value={description}
              placeholder="Role description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(permissionsByResource).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No permissions available. Seed permissions via the admin panel.
            </p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {Object.entries(permissionsByResource).map(([resource, perms]) => (
                <div key={resource}>
                  <p className="mb-2 text-sm font-semibold capitalize">{resource}</p>
                  <div className="flex flex-col gap-2">
                    {(perms ?? []).map((perm) => (
                      <label key={perm.id} className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={selectedPermissions.has(perm.id)}
                          onCheckedChange={() => togglePermission(perm.id)}
                        />
                        <span className="text-sm">{perm.name.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
